import { useState } from 'react'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Pencil, Share } from '@factorialco/f0-react/icons/app'
import {
  GetTaskDetailsDocument,
  TaskDetailFragment,
  TasksValueObjectsTaskStatusEnum,
  UpdateTaskDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import EditTaskModal from 'modules/tasks/components/EditTaskModal'
import { ShareTaskModal } from 'modules/tasks/components/TaskDetails/Header/Share/Modal'
import { ShareTaskProvider } from 'modules/tasks/components/TaskDetails/Header/Share/Provider'
import { isValidTaskStatus, taskStatuses } from 'modules/tasks/lib/status'

interface IHeaderProps {
  task: TaskDetailFragment
}

const Header = ({ task }: IHeaderProps) => {
  const { access } = useCurrentSession()
  const [showEditModal, setShowEditModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)

  const assignees = task?.assigneesConnection.nodes || []
  const assigneesCount = task?.assigneesConnection.totalCount || 0
  const taskDetail = { ...task, assignees, assigneesCount }

  const [updateTask, { loading: updateTasksLoading }] = useMutation(UpdateTaskDocument, {
    extractorFn: (data) => data.tasksMutations.updateTasksTask,
  })

  const onSelectorChange = async (selectedStatus: TasksValueObjectsTaskStatusEnum) => {
    if (selectedStatus === task.status) return

    const result = await updateTask({
      variables: { id: task.id, status: selectedStatus, identifier: task.identifier },
      refetchQueries: [GetTaskDetailsDocument],
    })

    const handled = handleResult(result, (_) => {})
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
  }

  const primaryActionItems = () => {
    return taskStatuses().map((taskStatus) => ({
      value: taskStatus.status.toString(),
      label: i18n.t('tasks.mark.as', { status: taskStatus.title.toLowerCase() }),
    }))
  }

  return (
    <>
      <ResourceHeader
        title={task.name}
        description={task.relatedResourceTitle ?? undefined}
        secondaryActions={[
          {
            label: i18n.t('common.share'),
            icon: Share,
            isVisible: task.author?.id === access.id,
            onClick: () => {
              setShowShareModal(true)
              track(trackingEvents.tasks.shareTaskOpened, {
                task_id: task.id,
              })
            },
          },
          {
            label: i18n.t('common.edit'),
            icon: Pencil,
            disabled: !task._policies?.edit.can,
            onClick: () => {
              setShowEditModal(true)
            },
          },
        ]}
        primaryAction={{
          disabled: updateTasksLoading || !task._policies?.resolve.can,
          items: primaryActionItems(),
          value: task.status === 'done' || task.status === 'discarded' ? 'todo' : 'done',
          onClick: (value) => {
            if (isValidTaskStatus(value)) {
              onSelectorChange(value)
            }
          },
        }}
      />
      {showEditModal && <EditTaskModal taskId={task.id} onClose={() => setShowEditModal(false)} />}
      {showShareModal && (
        <ShareTaskProvider task={taskDetail}>
          <ShareTaskModal task={taskDetail} onClose={() => setShowShareModal(false)} />
        </ShareTaskProvider>
      )}
    </>
  )
}

export default Header
